<template>
  <aside class="sidebar" :class="{ 'collapsed': isCollapsed }">
    <div class="sidebar-header">
      <button @click="requestToggle" class="sidebar-toggle-button" :title="isCollapsed ? '展開菜單' : '收合菜單'">
        <i class="fas fa-bars"></i>
      </button>
      <div class="user-profile-container" v-if="!isCollapsed">
        <img :src="pictureUrl || defaultAvatar" alt="使用者頭像" class="avatar" @click="$router.push('/')">
        <div class="user-info-text">
          <h2 class="user-id">{{ localUserName || '使用者' }}</h2>
          <p class="user-greeting">指揮官</p>
        </div>
      </div>
    </div>

    <nav class="main-nav">
      <ul>
        <li v-for="item in menuItems" :key="item.name" :title="isCollapsed ? item.name : ''">
          <router-link :to="item.route" class="nav-link">
            <i :class="['nav-icon', item.iconClass]"></i>
            <span class="nav-text" v-show="!isCollapsed">{{ item.name }}</span>
          </router-link>
        </li>
      </ul>
    </nav>

    <button @click="performLogout" class="logout-button" :title="isCollapsed ? '登出戰場' : ''">
      <i class="fas fa-sign-out-alt"></i>
      <span class="nav-text" v-show="!isCollapsed">登出戰場</span>
    </button>
  </aside>
</template>

<script>
import { logout } from '@/api/auth.api';
import { adminPath } from '@/router/paths';
import defaultUserAvatar from '@/assets/logo.png'; // 使用本地圖片

export default {
  name: 'SidebarMenu',
  props: {
    isCollapsed: {
      type: Boolean,
      required: true,
    },
    userId: {
      type: String,
      default: '',
    },
    pictureUrl: {
      type: String,
      default: '',
    },
    displayName: {
      type: String,
      default: '',
    }
  },
  data() {
    return {
      defaultAvatar: defaultUserAvatar, // 使用導入的本地圖片
      menuItems: [
        { name: '首頁輪播圖', route: adminPath('carousel'), iconClass: 'fas fa-images' },
        { name: '商品管理', route: adminPath('products'), iconClass: 'fas fa-cubes' },
        { name: '類別管理', route: adminPath('categories'), iconClass: 'fas fa-sitemap' },
        { name: '商品模板管理', route: adminPath('templates'), iconClass: 'fas fa-file-alt' },
        { name: '會員管理', route: adminPath('members'), iconClass: 'fas fa-users' },
        { name: '文章管理', route: adminPath('article'), iconClass: 'fas fa-bullhorn' },
        { name: '文章分類管理', route: adminPath('article-categories'), iconClass: 'fas fa-folder' },
        { name: '文章標籤管理', route: adminPath('article-tags'), iconClass: 'fas fa-tags' },
        { name: '公告欄', route: adminPath('announcements'), iconClass: 'fas fa-bullhorn' },
        { name: '訂單管理', route: adminPath('orders'), iconClass: 'fas fa-receipt' },
        { name: '預設載具管理', route: adminPath('default-carriers'), iconClass: 'fas fa-sim-card' },
        { name: '系統參數', route: adminPath('system-parameters'), iconClass: 'fas fa-cogs' },
      ]
    };
  },
  computed: {
    localUserName() {
      return this.displayName || '使用者';
    }
  },
  methods: {
    requestToggle() {
      this.$emit('toggle-request');
    },
    async performLogout() {
      // ... (登出邏輯不變) ...
      const token = localStorage.getItem('jwt');
      if (token) {
        try {
          await logout();
        } catch (err) {
          console.error("後端登出失敗:", err);
        } finally {
          localStorage.removeItem('jwt');
          this.$router.push('/login');
        }
      } else {
        this.$router.push('/login');
      }
    }
  }
};
</script>

<style scoped>
/* 基本側邊欄樣式 */
.sidebar {
  width: 260px;
  background-color: rgba(25, 25, 35, 0.9);
  border-right: 1px solid rgba(160, 100, 220, 0.4);
  box-shadow: 2px 0 15px rgba(0,0,0,0.5);
  display: flex;
  flex-direction: column;
  padding-bottom: 15px;
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;
  position: relative; /* 為了絕對定位的子元素，如 toggle button */
  color: #c0c0c0;
}

.sidebar.collapsed {
  width: 70px;
}

/* 側邊欄頭部 */
.sidebar-header {
  display: flex;
  align-items: center;
  padding: 15px; /* 統一 padding */
  border-bottom: 1px solid rgba(160, 100, 220, 0.2);
  min-height: 60px; /* 與 toggle button 的高度相關 */
  box-sizing: border-box;
  position: relative; /* 確保 toggle button 相對此定位 */
}

.sidebar.collapsed .sidebar-header {
  padding: 0; /* 收合時 header 的 padding 由 toggle button 控制 */
  justify-content: center; /* 使 toggle button (如果不是絕對定位) 居中 */
  /* 如果 toggle button 是絕對定位，這裡的 justify-content 可能不直接影響它 */
}

/* 收合按鈕 */
.sidebar-toggle-button {
  background: none;
  border: none;
  color: #adb5bd;
  font-size: 1.3em;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  transition: color 0.2s ease, background-color 0.2s ease;
  line-height: 1;
  /* 如果希望按鈕在展開時靠左，收合時也保持在左側 */
  position: absolute;
  top: 50%;
  left: 15px; /* 距離左邊的固定距離 */
  transform: translateY(-50%); /* 垂直居中 */
  z-index: 10; /* 確保在其他內容之上 */
}

.sidebar.collapsed .sidebar-toggle-button {
  left: calc( (70px - (1.3em + 16px) ) / 2 ); /* 大致計算使其在70px寬度內居中 (1.3em字體 + padding) */
  /* 或者固定 left: 15px; 也可以，看哪種效果更好 */
}


.sidebar-toggle-button:hover {
  color: #e0e0e0;
  background-color: rgba(255, 255, 255, 0.05);
}

/* 用戶信息容器 (展開時) */
.user-profile-container {
  display: flex;
  align-items: center;
  overflow: hidden;
  flex-grow: 1;
  margin-left: 50px; /* 為 toggle button 留出空間 */
  opacity: 1;
  visibility: visible;
  transition: opacity 0.2s ease 0.1s, visibility 0.2s ease 0.1s; /* 延遲出現 */
}
.sidebar.collapsed .user-profile-container {
  opacity: 0;
  visibility: hidden;
  transition-delay: 0s !important; /* 立即隱藏 */
  width: 0;
  margin-left: 0;
}


.avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid rgba(160, 100, 220, 0.6);
  margin-right: 10px;
  object-fit: cover;
  flex-shrink: 0;
  cursor: pointer;
}

.avatar:hover {
  transform: scale(1.05);
}

.user-info-text {
  display: flex;
  flex-direction: column;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-id {
  font-size: 0.95em;
  color: #e0e0e0;
  font-weight: 600;
  margin: 0;
}

.user-greeting {
  font-size: 0.8em;
  color: #888e95;
  margin: 0;
}

/* 導航菜單 */
.main-nav {
  flex-grow: 1;
  overflow-y: auto;
  margin-top: 10px; /* 與 header 的間隔 */
}

.main-nav ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.main-nav .nav-link {
  display: flex;
  align-items: center;
  padding: 12px 20px;
  color: #adb5bd;
  text-decoration: none;
  font-size: 0.95em;
  transition: background-color 0.2s ease, color 0.2s ease, padding-left 0.3s ease;
  white-space: nowrap;
  overflow: hidden;
}

.sidebar.collapsed .main-nav .nav-link {
  padding-left: 23px;
  justify-content: flex-start;
}

.main-nav .nav-link:hover {
  background-color: rgba(160, 100, 220, 0.1);
  color: #f8f9fa;
}

.main-nav .nav-link.router-link-exact-active {
  background-color: rgba(160, 100, 220, 0.2);
  color: #ffffff;
  font-weight: 500;
  border-left: 3px solid #8be9fd;
  padding-left: 17px;
}
.sidebar.collapsed .main-nav .nav-link.router-link-exact-active {
  padding-left: 20px; /* 23px - 3px border */
  border-left-width: 3px;
}


.nav-icon {
  margin-right: 12px;
  width: 18px;
  font-size: 1.05em;
  text-align: center;
  color: #888e95;
  flex-shrink: 0;
  transition: margin-right 0.3s ease;
}
.sidebar.collapsed .nav-icon {
  margin-right: 0;
}
.main-nav .nav-link:hover .nav-icon,
.main-nav .nav-link.router-link-exact-active .nav-icon {
  color: #8be9fd;
}


.nav-text {
  opacity: 1;
  transition: opacity 0.15s ease 0.1s, visibility 0.15s ease 0.1s; /* 略微延遲消失 */
  visibility: visible;
}
.sidebar.collapsed .nav-text {
  opacity: 0;
  width: 0;
  visibility: hidden;
  transition-delay: 0s !important; /* 確保文字立即消失 */
}

/* 登出按鈕 */
.logout-button {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin: 15px 15px 5px 15px;
  padding: 10px 15px;
  background: rgba(255, 85, 85, 0.1);
  color: #ff8080;
  font-size: 0.9em;
  font-weight: 500;
  border: 1px solid rgba(255, 85, 85, 0.3);
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  overflow: hidden;
  flex-shrink: 0;
}
.sidebar.collapsed .logout-button {
  width: auto;
  padding: 8px;
  justify-content: center;
  margin-left: auto;
  margin-right: auto;
}

.logout-button:hover {
  background: rgba(255, 85, 85, 0.2);
  color: #ff6666;
  border-color: rgba(255, 85, 85, 0.5);
}

.logout-button i {
  margin-right: 10px;
  font-size: 1em;
  transition: margin-right 0.3s ease;
}
.sidebar.collapsed .logout-button i {
  margin-right: 0;
}
</style>
